import { AppShell } from '@components/layout';
import { AnalyticsPage } from '@features/analytics';
import { ProtectedRoute } from '@features/auth';
import { DataSyncPage } from '@features/datasync';
import { EnvironmentsPage } from '@features/environments';
import {
  BlackboardOAuthRedirect,
  CanvasOAuthRedirect,
  DocuSignOAuthRedirect,
  SalesforceOAuthRedirect,
  ZoomOAuthRedirect,
} from '@features/environments/components/credentials/oauth';
import {
  EventsObjectFormPage,
  EventsObjectsPage,
  EventsSubscriptionFormPage,
  EventsSubscriptionsPage,
} from '@features/events';
import { RecipeEditorFlow } from '@features/recipe-editor';
import NewRecipePage from '@features/recipe-editor/pages/NewRecipePage';
import { SchedulesPage } from '@features/schedules';
import {
  AccountSettingsIndex,
  AccountSettingsLayout,
  DataProcessingHours,
  OrganizationUsers,
  ProjectUsers,
  TenantSettings,
} from '@features/tenant-settings';
import {
  ExecutionDetailsPage,
  ExecutionsListPage,
  RecipesListPage,
  SfJobFailedResultsPage,
  SfJobSuccessResultsPage,
  SfJobsPage,
} from '@features/transformers';
import {
  NewWizardPage,
  WizardConfigPage,
  WizardExecutionDetailsPage,
  WizardFormPage,
  WizardListPage,
} from '@features/wizards';
import { WorkspaceLayout } from '@features/workspace';
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router';

import {
  CallbackPage,
  DashboardPage,
  HomePage,
  LoginPage,
  NewWorkspacePage,
  NotFoundPage,
  SettingsPage,
  TeamPage,
} from './pages';
import { ROUTES } from './routeConfig';

/**
 * Main application routes
 */
export const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.CALLBACK} element={<CallbackPage />} />

      {/* OAuth callback routes */}
      <Route path={ROUTES.SF_OAUTH} element={<SalesforceOAuthRedirect />} />
      <Route path={ROUTES.DS_OAUTH} element={<DocuSignOAuthRedirect />} />
      <Route path={ROUTES.BS_OAUTH} element={<BlackboardOAuthRedirect />} />
      <Route path={ROUTES.ZOOM_OAUTH} element={<ZoomOAuthRedirect />} />
      <Route path={ROUTES.CANVAS_OAUTH} element={<CanvasOAuthRedirect />} />

      {/* Invitation link: /invitations/:feedKey → store key, redirect through login */}
      <Route path={ROUTES.INVITATIONS} element={<InvitationRedirect />} />

      {/* Public data mapper */}
      <Route path={ROUTES.DATA_MAPPING} element={<div>Public Data Mapping</div>} />

      {/* Protected routes */}
      <Route
        path={ROUTES.HOME}
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />

      {/* App workspace routes */}
      <Route
        path="/a/:appId"
        element={
          <ProtectedRoute>
            <WorkspaceLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard routes */}
        <Route path="dash" element={<DashboardPage />} />
        <Route path="dash/team" element={<TeamPage />} />
        <Route path="dash/settings" element={<SettingsPage />} />

        {/* Environment routes — single wildcard keeps EnvironmentsPage mounted across sub-paths */}
        <Route path="env" element={<EnvironmentsPage />} />
        <Route path="env/:envId/*" element={<EnvironmentsPage />} />

        {/* Recipe routes */}
        <Route path="tf" element={<RecipesListPage />} />
        <Route path="tf/new" element={<NewRecipePage />} />
        <Route path="tf/:recipeId" element={<RecipeEditorFlow />} />

        {/* Execution routes - smart router based on query params */}
        <Route path="health" element={<ExecutionHealthRouter />} />

        {/* Salesforce Bulk Jobs Info pages */}
        <Route path="health/sfjobs" element={<SfJobsPage />} />
        <Route path="health/sfjobs/failed" element={<SfJobFailedResultsPage />} />
        <Route path="health/sfjobs/successful" element={<SfJobSuccessResultsPage />} />

        {/* Wizard execution health (separate route) */}
        <Route path="wiz/health" element={<WizardExecutionDetailsPage />} />

        {/* Event routes */}
        <Route path="events" element={<EventsSubscriptionsPage />} />
        <Route path="events/obj" element={<EventsObjectsPage />} />
        <Route path="events/obj/new" element={<EventsObjectFormPage />} />
        <Route path="events/obj/:objId/edit" element={<EventsObjectFormPage />} />
        <Route path="events/sub/new" element={<EventsSubscriptionFormPage />} />

        {/* Schedule routes */}
        <Route path="schedule" element={<SchedulesPage />} />

        {/* Analytics routes */}
        <Route path="ca" element={<AnalyticsPage />} />

        {/* Lingk APIs / DataSync routes */}
        <Route path="db" element={<DataSyncPage />} />

        {/* Data Mappings - disabled, not in use */}
        {/* <Route path="map" element={<DataMappingPage />} /> */}

        {/* Wizard routes */}
        <Route path="wiz" element={<WizardListPage />} />
        <Route path="wiz/new" element={<NewWizardPage />} />
        <Route path="wiz/start/:wizardID" element={<WizardFormPage />} />
        <Route path="wiz/:transId" element={<WizardConfigPage />} />
        <Route path="wiz/:transId/configure" element={<WizardFormPage />} />

        {/* Default to dashboard */}
        <Route index element={<DashboardPage />} />
      </Route>

      {/* Account Settings routes - nested under /account-settings */}
      <Route
        path="account-settings"
        element={
          <ProtectedRoute>
            <AppShell hideSidebar>
              <AccountSettingsLayout />
            </AppShell>
          </ProtectedRoute>
        }
      >
        {/* Redirect /account-settings root to the first organization's Users page.
            Themes was the previous landing page but has been hidden from the sidebar. */}
        <Route index element={<AccountSettingsIndex />} />

        {/* <Route path="themes" element={<ThemesPage />} /> */}
        {/* Organization routes - nested under /account-settings/organizations/:orgId */}
        <Route path="organizations/:orgId" element={<TenantSettings />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<OrganizationUsers />} />
          <Route path="projects" element={<ProjectUsers />} />
          <Route path="projects/:projectId/users" element={<ProjectUsers />} />
          <Route path="data-processing-hours" element={<DataProcessingHours />} />
        </Route>
      </Route>

      {/* New workspace */}
      <Route
        path={ROUTES.NEW_ACCOUNT}
        element={
          <ProtectedRoute>
            <NewWorkspacePage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

/**
 * Handles /invitations/:feedKey links.
 * Stores the invite key in sessionStorage so CallbackPage can process it
 * after Auth0 authentication, then redirects to login with signup hint.
 */
const InvitationRedirect = () => {
  const { feedKey } = useParams<{ feedKey: string }>();
  if (feedKey) {
    sessionStorage.setItem('invite_subscriber_key', feedKey);
  }
  const returnTo = btoa('/');
  return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
};

/**
 * Router for /health - shows either ExecutionsList or ExecutionDetails
 * based on presence of executionGuid query parameter
 *
 * Examples:
 * - /a/123/health → ExecutionsList (all executions)
 * - /a/123/health?recipe=8212 → ExecutionsList (filtered by recipe)
 * - /a/123/health?executionGuid=xxx&env=yyy → ExecutionDetails (single execution view)
 */
const ExecutionHealthRouter = () => {
  const [searchParams] = useSearchParams();
  const executionGuid = searchParams.get('executionGuid');

  // If executionGuid exists, show details; otherwise show list
  return executionGuid ? <ExecutionDetailsPage /> : <ExecutionsListPage />;
};
